import React from 'react';
import _ from 'lodash';
import { Table } from 'reactstrap';
import domainModel from 'domain-model';
import { calculateDaysEarlierWeekday, weekdays, weekdayMap } from './common';

type DeliveryRulesEditorType = typeof domainModel.order.DeliveryRulesEditor;
type DeliveryRouteType = typeof domainModel.order.DeliveryRule;

const { getWeekdayName, DeliveryRulesGeneralRegion } =
  domainModel.deliveryRules;

function ExtendedRuleRowViewer({
  weekday,
  region,
  date,
  deliveryRule,
  pickAndPackRule,
}: {
  weekday?: string;
  region?: string;
  date?: string;
  deliveryRule?: DeliveryRouteType;
  pickAndPackRule?: DeliveryRouteType;
}) {
  const key: string = (weekday || date)!;
  const daysBefore = deliveryRule?.getDaysBefore() ?? 0;
  const daysEarlierWeekday = calculateDaysEarlierWeekday(
    weekday,
    daysBefore,
    date,
  );
  const daysEarlierText = getWeekdayName(daysEarlierWeekday);

  const isBlocked = deliveryRule?.isBlocked() || pickAndPackRule;

  return (
    <tr key={`${region} + ${key} `}>
      {weekday && (
        <td style={{ verticalAlign: 'middle' }}>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            {weekdayMap[weekday]?.value}
          </label>
        </td>
      )}
      {!weekday && !!date && (
        <td style={{ verticalAlign: 'middle' }}>
          <label style={{ display: 'flex', alignItems: 'center' }}>
            <span>{date}</span>
          </label>
        </td>
      )}
      {isBlocked && (
        <td>
          {deliveryRule?.isBlocked() && (
            <>
              <span>Ingen leverans denna dag</span>
              <br />
            </>
          )}
          {pickAndPackRule && (
            <>
              <span>Ingen plock och pack denna dag</span>
              <br />
            </>
          )}
        </td>
      )}
      {!isBlocked && deliveryRule && <td>{deliveryRule.getTime()}</td>}
      {!isBlocked && deliveryRule && (
        <td>
          <span>
            {deliveryRule.getDaysBefore() > 0
              ? `${daysEarlierText} (${deliveryRule.getDaysBefore()} ${
                  deliveryRule.getDaysBefore() === 1 ? 'dag' : 'dagar'
                } tidigare)`
              : 'Samma dag'}
          </span>
        </td>
      )}
    </tr>
  );
}

interface ExtendedRulesViewerProps {
  region: string;
  deliveryRulesEditor?: DeliveryRulesEditorType;
  onlyExplicitDates?: boolean;
}

const ExtendedRulesViewer: React.FC<ExtendedRulesViewerProps> = props => {
  const { deliveryRulesEditor, region, onlyExplicitDates } = props;

  let rulesName = `Regionspecifika leveransregler ${region}`;
  if (region === DeliveryRulesGeneralRegion) {
    rulesName = 'Generella leveransregler';
  }

  const rulesForRegionForDate = deliveryRulesEditor
    .getRulesWithRegion(region)
    .filter((deliveryRule: DeliveryRouteType) => deliveryRule.getDate());
  const blockedPickAndPackRules =
    deliveryRulesEditor.getPickAndPackBlockedRules();

  const datesForAllRules = _(rulesForRegionForDate)
    .concat(blockedPickAndPackRules)
    .map((x: DeliveryRouteType) => x.getDate())
    .uniq()
    .value();

  return (
    <div>
      {!onlyExplicitDates && <h4>{rulesName}</h4>}
      {!onlyExplicitDates && (
        <Table>
          <thead>
            <tr>
              <th>Leveransdag</th>
              <th>Bryttid</th>
              <th>Beställningsdag</th>
            </tr>
          </thead>
          <tbody>
            {weekdays.map(weekday => {
              const deliveryRule = deliveryRulesEditor.getRuleWithRegionAndKey(
                region,
                weekday.key,
              );
              if (!deliveryRule) return null;
              return (
                <ExtendedRuleRowViewer
                  key={weekday.key}
                  weekday={weekday.key}
                  region={region}
                  deliveryRule={deliveryRule}
                />
              );
            })}
          </tbody>
        </Table>
      )}

      {datesForAllRules.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Leveransdag</th>
              <th>Bryttid</th>
              <th>Beställningsdag</th>
            </tr>
          </thead>
          <tbody>
            {datesForAllRules.map(date => (
              <ExtendedRuleRowViewer
                key={date}
                date={date}
                region={region}
                deliveryRule={deliveryRulesEditor.getRuleWithRegionAndKey(
                  region,
                  date,
                )}
                pickAndPackRule={deliveryRulesEditor.getBlockedPickAndPackRuleWithDate(
                  date,
                )}
              />
            ))}
          </tbody>
        </Table>
      )}
      <hr />
    </div>
  );
};

export default ExtendedRulesViewer;
